import { useState, type ReactNode } from 'react';
import type { CanvasChatMessage } from '@/pages/canvas/canvas-types';
import { ChatToolProgressCard } from './ChatToolProgressCard';
import { PlaybookTaskCard } from './PlaybookTaskCard';
import { summarizeCanvasProgress } from '@/pages/canvas/canvas-progress-summary';
import { ChatThinkingLabel } from '@/pages/canvas/canvas-chat-status';

/** Tool and playbook events describe the same request. Upgrade its existing
 * card in place, keeping disclosure state local to this message/run. */
export function CanvasRunProgressCard({ message, isActive, queueContent, waiting = false }: { message: CanvasChatMessage; isActive: boolean; queueContent?: ReactNode; waiting?: boolean }) {
  const [expanded, setExpanded] = useState(true);
  const [planningExpanded, setPlanningExpanded] = useState(false);
  const planningGroups = message.conceptsRun?.autoBuild && message.conceptsRun.status === 'ready'
    ? (message.agentTaskGroups ?? []).filter(group => group.steps.some(step => step.toolName === 'angle_writer')) : [];
  const currentGroups = planningGroups.length ? (message.agentTaskGroups ?? []).filter(group => !planningGroups.includes(group)) : message.agentTaskGroups ?? [];
  const afterPlanning = !!planningGroups.length;

  const hasImageGallery = message.imageSlots?.some(slot => !slot.failed && !!slot.resolved)
    || (!message.imageSlots?.length && !!message.thumbnails?.length);
  const hasTile = !waiting && (!!message.playbookProgress || !!message.agentTaskGroups?.length);
  const status = isActive ? '' : summarizeCanvasProgress(afterPlanning ? {...message,conceptsRun:undefined,agentTaskGroups:currentGroups} : message, false)[0];
  const hideCompletedStatus = hasImageGallery && (status === 'Recorded work is complete.' || status === 'Ad concepts are ready.');
  return <div data-testid={`canvas-run-progress-${message.id}`}>
    {(hasTile && (isActive || !hideCompletedStatus)) && <div role="status" style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 13, color: "#777b80", marginBottom: 8 }}>
      {isActive ? <span><ChatThinkingLabel /></span> : !hideCompletedStatus && <span>{status}</span>}
    </div>}
    {afterPlanning && <ChatToolProgressCard groups={planningGroups} isStreaming={false} expanded={planningExpanded} onExpandedChange={setPlanningExpanded} />}
    <div id={`run-process-${message.id}`}>{!hasTile ? (
      queueContent && <div data-testid="queued-process-tile" style={{background:"#eceef3",borderRadius:24,padding:"20px 24px",margin:"12px 0"}}>{queueContent}</div>
    ) : message.playbookProgress ? (
      <PlaybookTaskCard progress={message.playbookProgress} messageId={message.id}
        messageImageSlots={message.imageSlots} isActive={isActive}
        showThumbnails={!hasImageGallery}
        footer={queueContent} expanded={expanded} onExpandedChange={setExpanded} />
    ) : (
      <ChatToolProgressCard groups={currentGroups} isStreaming={isActive}
        conceptDraftProgress={afterPlanning ? undefined : message.conceptDraftProgress} conceptCount={afterPlanning ? undefined : message.conceptsRun?.count}
        footer={queueContent} expanded={expanded} onExpandedChange={setExpanded} />
    )}</div>
  </div>;
}
